import { inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  Params,
  RedirectCommand,
  Router,
} from '@angular/router';

/**
 * Drops `rd` from the address of the pages that never use it.
 *
 * `rd` names the workspace someone came from. Exactly one page reads it: the
 * two-factor settings page, to offer a way back to that workspace — and only
 * after matching it against the workspaces the account can still reach. The
 * sign-in, code and workspace-list pages used to pass it along without ever
 * reading it, and sso-be ignores it at sign-in, so the address kept naming a
 * workspace that had nothing to do with what happened next — including one
 * whose SSO had since been switched off.
 *
 * Runs before the page is created, in place of the entry, keeping every other
 * parameter and the navigation's state (the sign-in page's notice rides on it).
 */
export const dropReturnHintGuard: CanActivateFn = (route: ActivatedRouteSnapshot) => {
  if (!route.queryParamMap.has('rd')) return true;

  const router = inject(Router);
  const queryParams: Params = { ...route.queryParams };
  delete queryParams['rd'];
  const path = route.pathFromRoot.flatMap((r) => r.url.map((segment) => segment.path));
  return new RedirectCommand(
    router.createUrlTree(['/', ...path], {
      queryParams,
      fragment: route.fragment ?? undefined,
    }),
    {
      replaceUrl: true,
      state: router.getCurrentNavigation()?.extras.state,
    },
  );
};
